import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Zap, Target, Loader2 } from 'lucide-react';
import { GolferPOVTrajectory } from './GolferPOVTrajectory';
import { TrajectoryChart } from './TrajectoryChart';
import { useAIRecommendations } from '../../hooks/useAIRecommendations';
import { useTrajectorySimulator } from '../../hooks/useTrajectorySimulator';
import { useSimulationResult } from '../../hooks/useSimulationResult';

export const AIRecommendationsView = ({
  shotGroups,
  selectedShots = [],
  distanceType = 'carry'
}) => {
  const { tips, presets, loading } = useAIRecommendations(shotGroups);
  const {
    simulatedParams,
    setSimulatedParams,
    applyPreset,
    updateMetric,
    resetToPreset
  } = useTrajectorySimulator(presets);

  // 6DOF physics simulation — runs when sliders change (debounced 300ms)
  const { simulation, loading: simLoading } = useSimulationResult(simulatedParams);

  // Initialize simulator with presets once loaded
  useEffect(() => {
    if (presets.current && !simulatedParams) {
      setSimulatedParams(presets.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presets]);

  const rankIcons = {
    1: '🥇',
    2: '🥈',
    3: '🥉'
  };

  return (
    <div className="space-y-6">
      {/* Interactive Simulator Section */}
      <div>
        <h3 className="text-md font-semibold text-text-primary mb-4">
          Live Simulation
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Charts Section (3 columns) */}
          <div className="lg:col-span-3 space-y-4">
            {/* Split view: Spray Chart + Trajectory */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-surface border border-border rounded-lg p-4">
                <h4 className="text-sm font-semibold text-text-primary mb-2">Golfer POV</h4>
                <GolferPOVTrajectory
                  shotGroups={shotGroups}
                  selectedShots={selectedShots}
                  distanceType={distanceType}
                  simulationMode={true}
                  simulatedParams={simulatedParams}
                  simulation={simulation}
                />
              </div>
              <div className="bg-surface border border-border rounded-lg p-4">
                <h4 className="text-sm font-semibold text-text-primary mb-2 flex items-center gap-2">
                  Trajectory Arc
                  {simLoading && <Loader2 size={14} className="animate-spin text-primary" />}
                </h4>
                <TrajectoryChart
                  shotGroups={shotGroups}
                  selectedShots={selectedShots}
                  showAverage={true}
                  simulationMode={true}
                  simulatedParams={simulatedParams}
                  simulation={simulation}
                />
              </div>
            </div>

            {/* 6DOF Simulation Summary */}
            {simulation?.summary && (
              <div className="bg-surface border border-border rounded-lg p-4 grid grid-cols-3 sm:grid-cols-6 gap-3 text-center">
                <div>
                  <div className="text-lg font-bold text-white">{simulation.summary.carry}</div>
                  <div className="text-[10px] text-text-muted">Carry (yds)</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-white">{simulation.summary.total}</div>
                  <div className="text-[10px] text-text-muted">Total (yds)</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-blue-400">{simulation.summary.max_height_yards}</div>
                  <div className="text-[10px] text-text-muted">Apex (yds)</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-white">{simulation.summary.descent_angle}</div>
                  <div className="text-[10px] text-text-muted">Descent (°)</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-white">{simulation.summary.offline_yards || 0}</div>
                  <div className="text-[10px] text-text-muted">Offline (yds)</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-white">{simulation.summary.flight_time}</div>
                  <div className="text-[10px] text-text-muted">Flight (s)</div>
                </div>
              </div>
            )}
          </div>

          {/* Parameter Controls (1 column) - Stacked vertically */}
          <div className="space-y-4 flex flex-col">
          <div className="bg-surface border border-border rounded-lg p-4 flex-1">
            <h4 className="text-sm font-semibold text-text-primary mb-3">Custom Adjustments</h4>
            {simulatedParams && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-text-secondary block mb-1">
                    Launch Angle: {simulatedParams.launch_angle?.toFixed(1)}°
                  </label>
                  <input
                    type="range"
                    min="8"
                    max="18"
                    step="0.5"
                    value={simulatedParams.launch_angle || 12}
                    onChange={(e) => updateMetric('launch_angle', e.target.value)}
                    className="w-full accent-yellow-500"
                  />
                </div>

                <div>
                  <label className="text-xs text-text-secondary block mb-1">
                    Spin Axis: {simulatedParams.spin_axis?.toFixed(1)}°
                  </label>
                  <input
                    type="range"
                    min="-5"
                    max="5"
                    step="0.1"
                    value={simulatedParams.spin_axis || 0}
                    onChange={(e) => updateMetric('spin_axis', e.target.value)}
                    className="w-full accent-yellow-500"
                  />
                </div>

                <div>
                  <label className="text-xs text-text-secondary block mb-1">
                    Spin Rate: {simulatedParams.spin_rate?.toFixed(0)} rpm
                  </label>
                  <input
                    type="range"
                    min="1500"
                    max="4000"
                    step="100"
                    value={simulatedParams.spin_rate || 2800}
                    onChange={(e) => updateMetric('spin_rate', e.target.value)}
                    className="w-full accent-yellow-500"
                  />
                </div>

                <div>
                  <label className="text-xs text-text-secondary block mb-1">
                    Ball Speed: {simulatedParams.ball_speed?.toFixed(1)} mph
                  </label>
                  <input
                    type="range"
                    min="100"
                    max="180"
                    step="1"
                    value={simulatedParams.ball_speed || 150}
                    onChange={(e) => updateMetric('ball_speed', e.target.value)}
                    className="w-full accent-yellow-500"
                  />
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

AIRecommendationsView.propTypes = {
  shotGroups: PropTypes.array.isRequired,
  selectedShots: PropTypes.array,
  distanceType: PropTypes.oneOf(['carry', 'total'])
};

export default AIRecommendationsView;
